class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = null;
    this.eventController = new AbortController();
    this.searchTerm = '';
    this.resultsMaxHeight = 0;
    this.statusTimeout = null;
    this.layerElement = null;

    if (!this.input || !this.predictiveSearchResults) return;

    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.eventController.abort();
    window.clearTimeout(this.statusTimeout);
    this.close();
  }

  setupEventListeners() {
    const signal = this.eventController.signal;

    this.input.form?.addEventListener('submit', this.onFormSubmit.bind(this), { signal });
    this.input.addEventListener('focus', this.onFocus.bind(this), { signal });
    this.addEventListener('focusout', this.onFocusOut.bind(this), { signal });
    this.addEventListener('keydown', this.onKeydown.bind(this), { signal });
    window.addEventListener('resize', this.onViewportChange.bind(this), { signal });
    window.visualViewport?.addEventListener('resize', this.onViewportChange.bind(this), { signal });
  }

  getQuery() {
    return this.input?.value.trim() || '';
  }

  onChange() {
    if (!this.input) return;

    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.toLocaleLowerCase().startsWith(this.searchTerm.toLocaleLowerCase())) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm) {
      this.abortController?.abort();
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery()) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (!this.getQuery()) {
      this.searchTerm = '';
      this.abortController?.abort();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(currentSearchTerm);
    }
  }

  onFocusOut() {
    window.setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 0);
  }

  onKeydown(event) {
    if (event.defaultPrevented) return;

    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      this.input.focus();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.getQuery()) return;

      event.preventDefault();
      if (!this.isOpen && this.getAttribute('results') === 'true') this.open();
      this.switchOption(event.key === 'ArrowUp' ? 'up' : 'down');
      return;
    }

    if (event.key === 'Enter' && this.querySelector('[role="option"][aria-selected="true"]')) {
      event.preventDefault();
      this.selectOption();
    }
  }

  onViewportChange() {
    if (!this.isOpen) return;

    this.normalizeResultGroupOrder();
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
  }

  escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  updateSearchForTerm(previousTerm, newTerm) {
    if (!previousTerm || previousTerm === newTerm) return;

    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.textContent;
    if (!currentButtonText) return;

    const previousTermPattern = new RegExp(this.escapeRegExp(previousTerm), 'gi');
    const matches = currentButtonText.match(previousTermPattern) || [];
    if (matches.length !== 1) return;

    searchForTextElement.textContent = currentButtonText.replace(previousTermPattern, newTerm);
  }

  getVisibleOptions() {
    return Array.from(this.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null && element.getAttribute('aria-disabled') !== 'true'
    );
  }

  switchOption(direction) {
    if (!this.isOpen) return;

    const options = this.getVisibleOptions();
    if (!options.length) return;

    const selectedElement = this.querySelector('[role="option"][aria-selected="true"]');
    const selectedIndex = options.indexOf(selectedElement);
    let activeElementIndex;

    if (direction === 'up') {
      activeElementIndex = selectedIndex <= 0 ? options.length - 1 : selectedIndex - 1;
    } else {
      activeElementIndex = selectedIndex < 0 || selectedIndex === options.length - 1 ? 0 : selectedIndex + 1;
    }

    for (const option of options) option.setAttribute('aria-selected', 'false');

    const activeElement = options[activeElementIndex];
    activeElement.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', activeElement.id || '');
    activeElement.scrollIntoView({ block: 'nearest' });

    if (this.statusElement) this.statusElement.textContent = '';
  }

  selectOption() {
    const selectedOption = this.querySelector('[role="option"][aria-selected="true"]');
    if (!selectedOption) return;

    const selectedLink = selectedOption.matches('a[href]')
      ? selectedOption
      : selectedOption.querySelector('a[href]');

    if (selectedLink) {
      selectedLink.click();
      return;
    }

    const selectedButton = selectedOption.matches('button')
      ? selectedOption
      : selectedOption.querySelector('button');
    selectedButton?.click();
  }

  async getSearchResults(searchTerm) {
    const predictiveSearchUrl = window.routes?.predictive_search_url;
    if (!predictiveSearchUrl || !this.predictiveSearchResults) {
      this.handleSearchFailure();
      return;
    }

    const queryKey = searchTerm.replace(/\s+/g, '-').toLocaleLowerCase();
    this.setLiveRegionLoadingState();

    if (Object.prototype.hasOwnProperty.call(this.cachedResults, queryKey)) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    const requestUrl = new URL(predictiveSearchUrl, window.location.origin);
    requestUrl.search = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[type]': 'product,query,collection,page,article',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
    }).toString();

    try {
      const response = await fetch(requestUrl.toString(), {
        signal: this.abortController.signal,
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error(`Predictive search request failed (${response.status})`);

      const responseText = await response.text();
      const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const responseSection = responseDocument.querySelector('#shopify-section-predictive-search');
      if (!responseSection) throw new Error('Predictive search response did not contain the expected section');
      if (this.getQuery() !== searchTerm) return;

      const resultsMarkup = responseSection.innerHTML;
      for (const predictiveSearchInstance of this.allPredictiveSearchInstances) {
        predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
      }

      this.renderSearchResults(resultsMarkup, searchTerm);
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 20) return;
      this.handleSearchFailure();
    }
  }

  handleSearchFailure() {
    this.removeAttribute('loading');
    this.setLiveRegionText('Search suggestions are temporarily unavailable. Press Enter to view search results.');
    this.close();
    document.dispatchEvent(
      new CustomEvent('avm:predictive-search-failed', {
        detail: { source: 'predictive-search' },
      })
    );
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text') || 'Loading search suggestions';
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    window.clearTimeout(this.statusTimeout);
    this.statusElement.removeAttribute('aria-hidden');
    this.statusElement.textContent = statusText || '';
    this.statusTimeout = window.setTimeout(() => {
      if (this.statusElement) this.statusElement.textContent = '';
    }, 3000);
  }

  namespaceResultIds() {
    const resultRoot = this.predictiveSearchResults.querySelector('#predictive-search-results');
    if (!resultRoot) return;

    const suffix = (this.input.id || `search-${Date.now()}`).replace(/[^a-zA-Z0-9_-]/g, '-');
    const idMap = new Map();

    for (const element of resultRoot.querySelectorAll('[id]')) {
      const previousId = element.id;
      const nextId = `${previousId}-${suffix}`;
      idMap.set(previousId, nextId);
      element.id = nextId;
    }

    for (const element of resultRoot.querySelectorAll('[aria-labelledby], [aria-controls]')) {
      for (const attribute of ['aria-labelledby', 'aria-controls']) {
        const value = element.getAttribute(attribute);
        if (!value) continue;
        element.setAttribute(
          attribute,
          value
            .split(/\s+/)
            .map((id) => idMap.get(id) || id)
            .join(' ')
        );
      }
    }

    const resultRootId = `predictive-search-results-${suffix}`;
    resultRoot.id = resultRootId;
    this.input.setAttribute('aria-controls', resultRootId);
    this.input.setAttribute('aria-owns', resultRootId);
  }

  normalizeSearchValue(value) {
    return (value || '')
      .toLocaleLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  rankProductResults(searchTerm) {
    const productList = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');
    if (!productList) return;

    const normalizedQuery = this.normalizeSearchValue(searchTerm);
    const productItems = Array.from(productList.children);

    for (const item of productItems) {
      const normalizedTitle = this.normalizeSearchValue(item.dataset.productTitle);
      const exactTerms = (item.dataset.exactTerms || '')
        .split(/[|,;]/)
        .map((term) => this.normalizeSearchValue(term))
        .filter(Boolean);
      const exactTitle = normalizedTitle === normalizedQuery;
      const exactMetadata = !exactTitle && exactTerms.includes(normalizedQuery);
      const available = item.dataset.available === 'true';

      item.dataset.rank = exactTitle ? '0' : available && exactMetadata ? '1' : available ? '2' : exactMetadata ? '3' : '4';
      item.classList.toggle('predictive-search__list-item--exact', exactTitle || exactMetadata);
      item.querySelector('[data-predictive-exact-badge]')?.toggleAttribute('hidden', !(exactTitle || exactMetadata));
    }

    productItems
      .sort((first, second) => Number(first.dataset.rank) - Number(second.dataset.rank))
      .forEach((item) => productList.appendChild(item));
  }

  normalizeResultGroupOrder() {
    const wrapper = this.predictiveSearchResults.querySelector('[id^="predictive-search-results-groups-wrapper"]');
    if (!wrapper) return;

    const groups = Array.from(wrapper.children).filter((element) =>
      element.classList.contains('predictive-search__result-group')
    );
    const productGroup = groups.find((group) => group.querySelector('[id^="predictive-search-results-products-list"]'));
    const suggestionGroup = groups.find((group) => group !== productGroup);
    if (!productGroup || !suggestionGroup) return;

    if (window.matchMedia('(max-width: 749px)').matches) wrapper.prepend(productGroup);
    else wrapper.prepend(suggestionGroup);
  }

  renderSearchResults(resultsMarkup, searchTerm = this.getQuery()) {
    if (!this.predictiveSearchResults || typeof resultsMarkup !== 'string') {
      this.handleSearchFailure();
      return;
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.rankProductResults(searchTerm);
    this.normalizeResultGroupOrder();
    this.namespaceResultIds();
    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(countElement?.textContent?.trim() || 'Search suggestions updated');
  }

  getResultsMaxHeight() {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const inputBottom = this.input?.getBoundingClientRect().bottom || 0;
    return Math.max(120, Math.floor(viewportHeight - inputBottom - 8));
  }

  updatePageOpenState() {
    const hasOpenPredictiveSearch = Boolean(document.querySelector('predictive-search[open]'));
    document.documentElement.classList.toggle('predictive-search-is-open', hasOpenPredictiveSearch);
    document.body?.classList.toggle('predictive-search-is-open', hasOpenPredictiveSearch);

    let backdrop = document.querySelector('[data-predictive-search-backdrop]');
    if (hasOpenPredictiveSearch && !backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'predictive-search__backdrop';
      backdrop.dataset.predictiveSearchBackdrop = '';
      backdrop.setAttribute('aria-hidden', 'true');
      backdrop.addEventListener('click', () => {
        const openSearch = document.querySelector('predictive-search[open]');
        openSearch?.close();
        openSearch?.input?.focus();
      });
      document.body?.appendChild(backdrop);
    } else if (!hasOpenPredictiveSearch) {
      backdrop?.remove();
    }
  }

  open() {
    if (!this.predictiveSearchResults || !this.getQuery()) return;

    for (const predictiveSearchInstance of this.allPredictiveSearchInstances) {
      if (predictiveSearchInstance !== this && predictiveSearchInstance.hasAttribute('open')) {
        predictiveSearchInstance.close();
      }
    }

    this.layerElement = this.closest('.shopify-section') || this;
    this.layerElement.classList.add('predictive-search-layer');
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    this.updatePageOpenState();
    document.dispatchEvent(new CustomEvent('predictive-search:open'));
  }

  close(clearSearchTerm = false) {
    const wasOpen = this.isOpen;
    this.closeResults(clearSearchTerm);
    this.isOpen = false;

    if (wasOpen) document.dispatchEvent(new CustomEvent('predictive-search:close'));
  }

  closeResults(clearSearchTerm = false) {
    if (!this.input || !this.predictiveSearchResults) return;

    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');
    }

    for (const selected of this.querySelectorAll('[role="option"][aria-selected="true"]')) {
      selected.setAttribute('aria-selected', 'false');
    }

    this.input.removeAttribute('aria-activedescendant');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.resultsMaxHeight = 0;
    this.predictiveSearchResults.removeAttribute('style');
    this.layerElement?.classList.remove('predictive-search-layer');
    this.layerElement = null;
    this.updatePageOpenState();
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
